<script setup>
// ============================================================================
// IMPORTS
// ============================================================================

// Vue Core
import {
  ref,
  reactive,
  watch,
  onMounted,
  onBeforeUnmount,
  defineAsyncComponent,
  nextTick
} from 'vue'

// Vue Router
import { useRoute, useRouter } from 'vue-router'

// Pinia Stores
import { useUserStore } from '@/stores/user'
import { useCustomerStore } from '@/stores/customer'
import { useZendeskStore } from '@/stores/zendesk'
import { useSalesStore } from '@/stores/sales'
import { useWebexStore } from '@/stores/webex'

// Plugins
import { api } from '@/plugins/axios'
import { sentryUser, sentryException, getSentryFeedback } from '@/plugins/sentry'
import * as Sentry from '@sentry/vue'
import event from '@/plugins/events'
import { TRIGGER_CONFIRM_DIALOG } from '@/plugins/events'
import { TRIGGER_ACTIVATION_INSTRUCTION_DIALOG } from './plugins/events'

// Composables
import { useDemoLogic } from '@/use/demoLogic'
import { useWebex } from '@/use/webex'
import html2canvas from 'html2canvas'
import { setupAlertEvents, useAlert } from '@/use/alert'

// ============================================================================
// COMPOSABLES & STORES
// ============================================================================

// Router composables
const route = useRoute()
const router = useRouter()

// Store instances
const userStore = useUserStore()
const customerStore = useCustomerStore()
const zendeskStore = useZendeskStore()
const salesStore = useSalesStore()
const webexStore = useWebexStore()

// Custom composables
const { isDemoMode } = useDemoLogic()
const { disconnect: disconnectWebex } = useWebex()

// ============================================================================
// ENVIRONMENT & CONFIGURATION
// ============================================================================

const pollMinutes = import.meta.env.VITE_JWT_POLL_MINUTES || 10

// ============================================================================
// DIALOG SYSTEM
// ============================================================================

// Dialog state
const showDemoNotice = ref(false)
const confirmDialog = reactive({
  open: false,
  bind: null
})
const activationInstruction = reactive({
  open: false,
  html: null
})
const updateCustomerDialog = reactive({
  open: false,
  customerData: null
})
const mergeDialog = reactive({
  open: false,
  customers: [],
  primaryEmail: null
})
const setTicketIdDialog = reactive({
  open: false
})

// Dialog methods
const triggerConfirmDialog = (data) => {
  if (data) {
    if (confirmDialog.open) return
    if (!data.onNo) data.onNo = () => (confirmDialog.open = false)
    confirmDialog.bind = data
    confirmDialog.open = true
  } else {
    confirmDialog.open = false
  }
}

const triggerActivationInstructionDialog = (html) => {
  activationInstruction.open = true
  activationInstruction.html = html
}

const triggerUpdateCustomerDialog = (customerData) => {
  updateCustomerDialog.customerData = customerData
  updateCustomerDialog.open = true
}

const triggerMergeDialog = ({ customers, primaryEmail }) => {
  mergeDialog.customers = customers || []
  mergeDialog.primaryEmail = primaryEmail ?? null
  mergeDialog.open = true
}

const closeMergeDialog = () => {
  mergeDialog.open = false
  mergeDialog.customers = []
  mergeDialog.primaryEmail = null
}

const triggerSetTicketIdDialog = () => {
  setTicketIdDialog.open = true
}

// ============================================================================
// TICKET SYSTEM
// ============================================================================

// Ticket state
const openTicketComment = ref(false)
const feedbackOpen = ref(false)
const feedbackInitialScreenshot = ref(null)

const openFeedback = async () => {
  try {
    const canvas = await html2canvas(document.body, { useCORS: true, logging: false })
    feedbackInitialScreenshot.value = canvas.toDataURL('image/png')
  } catch {
    feedbackInitialScreenshot.value = null
  }
  feedbackOpen.value = true
}

// Sentry feedback state
const sentryFeedback = ref(null)

// Ticket methods
const toggleTicketComment = (open) => {
  if (!open && openTicketComment.value) {
    openTicketComment.value = false
  } else if (zendeskStore.validateTicket()) {
    openTicketComment.value = true
  }
}

// Helper to set Sentry scope with customer and selected subscription (for feedback)
const setSentryFeedbackScope = () => {
  const customerEmail = customerStore.getEmail
  if (customerEmail) {
    Sentry.setTag('customer_email', customerEmail)
    Sentry.setContext('customer', { email: customerEmail })
  }
  const sub = customerStore.getSelectedSubscription
  if (sub) {
    if (sub.product_category) Sentry.setTag('subscription_product_category', sub.product_category)
    if (sub.order_id != null) Sentry.setTag('subscription_order_id', String(sub.order_id))
    if (sub.product_id != null) Sentry.setTag('subscription_product_id', String(sub.product_id))
    if (sub.schema_source) Sentry.setTag('subscription_schema_source', sub.schema_source)
    Sentry.setContext('subscription', {
      product_category: sub.product_category,
      order_id: sub.order_id,
      product_id: sub.product_id,
      schema_source: sub.schema_source
    })
  }
}

// Sentry feedback methods
const openSentryFeedback = async () => {
  // Ensure feedback is initialized
  if (!sentryFeedback.value) {
    sentryFeedback.value = getSentryFeedback()
  }

  if (!sentryFeedback.value) {
    console.warn('Sentry feedback not available - Sentry may not be enabled or initialized')
    return
  }

  try {
    // Use createForm() to get full control - opens form directly without intermediate button
    if (typeof sentryFeedback.value.createForm === 'function') {
      const form = await sentryFeedback.value.createForm({
        showName: false,
        showEmail: false,
        colorScheme: 'system'
      })
      form.appendToDom()

      setSentryFeedbackScope()

      // Intercept form submission to ensure email and subscription are included
      await nextTick()
      const formElement = form.el || form.dom || form._el
      if (formElement) {
        const submitButton =
          formElement.querySelector('button[type="submit"]') ||
          formElement.querySelector('button.sentry-feedback-submit') ||
          formElement.querySelector('[data-sentry-submit]')
        if (submitButton) {
          submitButton.addEventListener('click', () => setSentryFeedbackScope(), {
            once: true,
            capture: true
          })
        }
        const actualForm =
          formElement.querySelector('form') || (formElement.tagName === 'FORM' ? formElement : null)
        if (actualForm) {
          actualForm.addEventListener('submit', () => setSentryFeedbackScope(), {
            once: true,
            capture: true
          })
        }
      }

      form.open()
    } else {
      console.error('createForm method not available on Sentry feedback')
    }
  } catch (error) {
    console.error('Failed to open Sentry feedback dialog:', error)
  }
}

// ============================================================================
// AUTHENTICATION SYSTEM
// ============================================================================

// Auth state
const logoutLoading = ref(false)
const refreshInterval = ref(null)

// Helper function to update Sentry user context
const updateSentryUser = () => {
  if (userStore.loggedIn) {
    const user = userStore.getUser
    if (user && user.user_id) {
      // Safely construct username with fallbacks
      const firstName = user.first_name || ''
      const lastName = user.last_name || ''
      const username =
        firstName || lastName
          ? `${firstName} ${lastName}`.trim()
          : user.email || user.username || `User ${user.user_id}`

      sentryUser({
        id: user.user_id,
        username: username,
        email: user.email || undefined
      })
    }
  } else {
    sentryUser(null)
  }
}

// Auth methods
const logout = async (reason) => {
  logoutLoading.value = true
  try {
    await disconnectWebex() // tear down the Webex session so logout isn't a phantom-agent state
  } catch (e) {
    sentryException(e) // capture silently — no user toast mid-logout, but keep it monitored
  }
  try {
    if (typeof reason != 'string') reason = null
    await userStore.logout()
    customerStore.logout()
    zendeskStore.logout()
    salesStore.logout()
    sentryUser(null)
  } catch (e) {
    event.error(e)
  }
  const loginQuery = {}
  if (reason) loginQuery.error = reason
  // Preserve current location as redirect so screen pop automation resumes after re-login.
  // Mirrors what the router guard does for unauthenticated navigations.
  if (route.name !== 'login') loginQuery.redirect = route.fullPath
  logoutLoading.value = false
  router.push({ name: 'login', query: loginQuery })
}

const refreshAuth = async () => {
  console.log(`LOGGED IN: ${userStore.loggedIn}`)
  if (userStore.loggedIn) {
    try {
      const { token } = await api.post('/login/refresh', null, {
        headers: {
          'x-api-key': import.meta.env.VITE_ADMIN_API_KEY,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
      console.log(`Assigning new auth token`)
      api.defaults.headers = { Authorization: `Bearer ${token}` }
      userStore.setToken(token)
    } catch (e) {
      console.error(`REFRESH AUTH ERROR:`, e)
      logout()
    }
  }
}

// ============================================================================
// WEBEX OAUTH RETURN
// ============================================================================

// The Webex callback redirects the browser back to APP_URL with ?webex_linked=1
// (or =0&error=...). That lands on the login route, where the nav drawer isn't
// mounted, so handle it here: flip the persisted link flag and forward a
// logged-in agent into the app so WebexConnect initializes the SDK on mount.
const handleWebexReturn = () => {
  if (route.query.webex_linked === undefined) return
  if (route.query.webex_linked === '1') {
    userStore.setWebexLinked(true)
  } else {
    event.error(`Webex linking failed${route.query.error ? `: ${route.query.error}` : ''}`)
  }
  if (userStore.loggedIn && route.name === 'login') {
    router.replace({ name: 'search' })
  } else {
    const query = { ...route.query }
    delete query.webex_linked
    delete query.error
    router.replace({ query })
  }
}

// A one-shot call from onMounted races two things that can delay the query string
// becoming visible on `route`: main.js mounts the app without awaiting
// router.isReady(), and LoginView's own onMounted forwards an already-logged-in
// agent to their landing route (a second, separate navigation) before this
// component's onMounted gets to run. Watching instead of calling once means this
// fires whenever `webex_linked` actually shows up on the resolved route, no
// matter which of those finishes first. `immediate: true` still covers the
// already-resolved case so this behaves exactly like the old call on mount.
watch(() => route.query.webex_linked, val => {
  if (val !== undefined) handleWebexReturn()
}, { immediate: true })

// ============================================================================
// CUSTOMER MANAGEMENT
// ============================================================================

// Customer methods
const clearCustomer = () => {
  router.push({ name: 'search' })
  customerStore.clear()
  salesStore.clear()
  zendeskStore.clear()
}

// ============================================================================
// WATCHERS
// ============================================================================

// Route query watchers
watch(
  () => route.query.sessionId,
  (newVal) => {
    if (newVal && newVal != '') customerStore.setCallSessionId(newVal)
  },
  { immediate: true }
)

watch(
  () => route.query.callSessionId,
  (newVal) => {
    if (newVal && newVal != '') customerStore.setCallSessionId(newVal)
  },
  { immediate: true }
)

watch(
  () => route.query.chatVisitorId,
  (newVal) => {
    if (newVal && newVal != '') customerStore.setChatVisitorId(newVal)
  },
  { immediate: true }
)

watch(
  () => route.query.hostname,
  (newVal) => {
    if (newVal && newVal != '') customerStore.setChatHostname(newVal)
  },
  { immediate: true }
)

// Customer watchers
watch(
  () => customerStore.getCustomer,
  (newVal) => {
    document.title = `CASH ${newVal ? `${customerStore.getName}` : 'Agent Support System'}`
  },
  { immediate: true }
)

// ============================================================================
// LIFECYCLE HOOKS
// ============================================================================

onMounted(async () => {
  // Register all event listeners synchronously before any awaits so they are
  // never missed — child components (SearchForm) mount and fire API calls first,
  // and a 401 from those calls will emit 'logout' before refreshAuth resolves.
  event.on(TRIGGER_CONFIRM_DIALOG, triggerConfirmDialog)
  event.on(TRIGGER_ACTIVATION_INSTRUCTION_DIALOG, triggerActivationInstructionDialog)
  event.on('openUpdateCustomerForm', triggerUpdateCustomerDialog)
  event.on('openMergeDialog', triggerMergeDialog)
  event.on('openSetTicketId', triggerSetTicketIdDialog)
  setupAlertEvents()
  event.on('openNewTicketComment', toggleTicketComment)
  event.on('logout', logout)

  if (userStore.loggedIn) {
    updateSentryUser()
    await refreshAuth()
    console.log('Auth Refreshed')
  }

  // refreshInterval.value = setInterval(refreshAuth, pollMinutes * 60 * 1000)

  // Initialize Sentry feedback
  const initSentryFeedback = async () => {
    await nextTick()
    sentryFeedback.value = getSentryFeedback()

    // Retry if Sentry isn't initialized yet
    if (!sentryFeedback.value) {
      setTimeout(() => {
        sentryFeedback.value = getSentryFeedback()
      }, 500)
    }
  }
  await initSentryFeedback()

  // Add global event processor to include customer email and selected subscription in all Sentry events
  Sentry.addEventProcessor((event, hint) => {
    const customerEmail = customerStore.getEmail
    if (customerEmail) {
      if (!event.tags) event.tags = {}
      event.tags.customer_email = customerEmail
      if (!event.contexts) event.contexts = {}
      event.contexts.customer = { email: customerEmail }
      if (!event.extra) event.extra = {}
      event.extra.customer_email = customerEmail
    }
    const sub = customerStore.getSelectedSubscription
    if (sub) {
      if (!event.tags) event.tags = {}
      if (sub.product_category) event.tags.subscription_product_category = sub.product_category
      if (sub.order_id != null) event.tags.subscription_order_id = String(sub.order_id)
      if (sub.product_id != null) event.tags.subscription_product_id = String(sub.product_id)
      if (sub.schema_source) event.tags.subscription_schema_source = sub.schema_source
      if (!event.contexts) event.contexts = {}
      event.contexts.subscription = {
        product_category: sub.product_category,
        order_id: sub.order_id,
        product_id: sub.product_id,
        schema_source: sub.schema_source
      }
      if (!event.extra) event.extra = {}
      event.extra.subscription_product_category = sub.product_category
      event.extra.subscription_order_id = sub.order_id
      event.extra.subscription_product_id = sub.product_id
      event.extra.subscription_schema_source = sub.schema_source
    }
    const cartList = salesStore.getCartList
    if (cartList?.length) {
      if (!event.contexts) event.contexts = {}
      event.contexts.sales_cart = {
        item_count: cartList.length,
        items: cartList.map(
          ({
            cart_id,
            master_cart_id,
            bundle_cart_id,
            transaction_type,
            offer_configuration_id,
            promo_code
          }) => ({
            cart_id,
            master_cart_id,
            bundle_cart_id,
            transaction_type,
            offer_configuration_id,
            promo_code
          })
        )
      }
    }
    const basket = salesStore.getBasket
    if (basket) {
      if (!event.contexts) event.contexts = {}
      event.contexts.sales_basket = {
        status: salesStore.getBasketStatus,
        order_id: basket.order_id,
        expires_at: basket.expires_at,
        opened_at: basket.opened_at,
        declined_at: basket.declined_at,
        total_before_tax: basket.total_before_tax,
        promo_code: basket.promo_code,
        ticket_id: basket.ticket_id
      }
    }
    return event
  })

  if (isDemoMode) showDemoNotice.value = true
})

onBeforeUnmount(() => {
  if (refreshInterval.value) clearInterval(refreshInterval.value)
  event.removeListener(TRIGGER_CONFIRM_DIALOG, triggerConfirmDialog)
  event.removeListener(TRIGGER_ACTIVATION_INSTRUCTION_DIALOG, triggerActivationInstructionDialog)
  event.removeListener('openUpdateCustomerForm', triggerUpdateCustomerDialog)
  event.removeListener('openMergeDialog', triggerMergeDialog)
  event.removeListener('openSetTicketId', triggerSetTicketIdDialog)
  event.removeListener('openNewTicketComment', toggleTicketComment)
  event.removeListener('logout', logout)
})

// ============================================================================
// COMPONENT DEFINITIONS
// ============================================================================

const AlertSystem = defineAsyncComponent(() => import('@/components/Shared/AlertSystem.vue'))
const NavigationDrawer = defineAsyncComponent(
  () => import('@/components/Shared/NavigationDrawer.vue')
)
const SessionDetails = defineAsyncComponent(() => import('@/components/Shared/SessionDetails.vue'))
const TicketCommentButton = defineAsyncComponent(
  () => import('@/components/Tickets/TicketCommentButton.vue')
)
const DemoNotice = defineAsyncComponent(() => import('@/components/Shared/DemoNotice.vue'))
const ConfirmDialog = defineAsyncComponent(() => import('@/components/Shared/ConfirmDialog.vue'))
const ActivationInstruction = defineAsyncComponent(
  () => import('@/components/Subscriptions/Activations/ActivationInstruction.vue')
)
const NewTicketComment = defineAsyncComponent(
  () => import('@/components/Tickets/TicketCommentNew.vue')
)
const UpdateCustomerForm = defineAsyncComponent(
  () => import('@/components/Customers/UpdateCustomerForm.vue')
)
const MergeCustomersForm = defineAsyncComponent(
  () => import('@/components/Customers/MergeCustomersForm.vue')
)
const SetTicketId = defineAsyncComponent(() => import('@/components/Tickets/SetTicketId.vue'))
const FeedbackDialog = defineAsyncComponent(() => import('@/components/Shared/FeedbackDialog.vue'))
const ReasonForContactForm = defineAsyncComponent(() => import('@/components/Customers/ReasonForContactForm.vue'))
const AICustomerBriefing = defineAsyncComponent(() => import('@/components/Customers/AICustomerBriefing.vue'))
const CallControlBar = defineAsyncComponent(() => import('@/components/Webex/CallControlBar.vue'))
</script>

<template lang="pug">
v-app#app
  NavigationDrawer(@clearCustomer="clearCustomer" @logout="logout")
  AlertSystem
  SessionDetails

  v-main(:class="{ 'v-main--no-reserve': route.name === 'login' }")
    router-view(:fluid="true")
    TicketCommentButton(@toggleTicketComment="toggleTicketComment")

  v-dialog(v-model="showDemoNotice")
    DemoNotice.mx-auto(width="50%", @close="showDemoNotice = false")

  v-dialog(v-model="confirmDialog.open" persistent)
    ConfirmDialog.mx-auto(width="33%" v-bind="confirmDialog.bind")

  v-dialog(v-model="activationInstruction.open" persistent)
    ActivationInstruction.mx-auto(width="50%" :html="activationInstruction.html" @close="activationInstruction.open = false")

  v-dialog(v-model="updateCustomerDialog.open" min-width="60%" max-width="900px" persistent)
    UpdateCustomerForm(:customerData="updateCustomerDialog.customerData" @close="updateCustomerDialog.open = false")

  v-dialog(v-model="mergeDialog.open" min-width="60%" max-width="900px" persistent)
    MergeCustomersForm(
      :customers="mergeDialog.customers"
      :primary-email="mergeDialog.primaryEmail"
      @close="closeMergeDialog"
    )

  v-dialog(v-model="setTicketIdDialog.open" persistent width="33%")
    SetTicketId(@close="setTicketIdDialog.open = false")

  ReasonForContactForm(v-if="userStore.loggedIn")

  AICustomerBriefing(v-if="userStore.loggedIn")

  CallControlBar(v-if="userStore.loggedIn && userStore.getWebexEnabled")

  v-overlay.justify-center.align-center(v-model="logoutLoading")
    v-progress-circular(color="primary", indeterminate, size="64")
  
  v-bottom-sheet(v-model="openTicketComment", persistent)
    NewTicketComment(@sent="openTicketComment = false" @close="openTicketComment = false")

  v-dialog(v-model="feedbackOpen" max-width="700")
    FeedbackDialog(:initial-screenshot="feedbackInitialScreenshot" @close="feedbackOpen = false; feedbackInitialScreenshot = null")

  v-btn.feedback-btn(
    prepend-icon="mdi-bug"
    size="default"
    color="warning"
    variant="flat"
    density="comfortable"
    elevation="2"
    :class="{ 'feedback-btn--call': webexStore.isConnected || webexStore.isConnecting }"
    @click="openFeedback"
  )
    span.feedback-btn-text Report Issue

  // v-btn#sentry-bug-button.sentry-bug-button(
  //   icon="mdi-bug"
  //   size="small"
  //   color="error"
  //   variant="flat"
  //   density="comfortable"
  //   elevation="2"
  //   @click="openSentryFeedback"
  // )

</template>

<style>
.vertical-center {
  margin: 0;
  position: absolute;
  top: 50%;
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
}
.notice-msg {
  left: 0% !important;
  bottom: 0% !important;
}
.no-decoration {
  text-decoration: none;
}
.front {
  z-index: 99999;
}

.v-bottom-sheet__content {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.v-bottom-sheet > .v-bottom-sheet__content.v-overlay__content {
  box-shadow: none;
}

/* Reserve space at the end of the scrollable content for the two fixed bottom elements —
   the Report Issue button (.feedback-btn) and the Webex call bar (CallControlBar publishes
   its live height as --webex-call-bar-height, 0 when down) — so page content such as the
   data-table pagination footer on admin pages never scrolls underneath them. !important
   beats Vuetify's own .v-main padding shorthand (matches the fixed-bar override pattern in
   NavigationDrawer.vue). */
.v-main {
  padding-bottom: calc(var(--webex-call-bar-height, 0px) + 32px) !important;
}
/* Login has no scrollable footer content to protect from the fixed feedback
   button, and the reserved space pushes the page past the background image's
   fill-height box, leaving a strip of the viewport uncovered below it. */
.v-main--no-reserve {
  padding-bottom: 0 !important;
}

.feedback-btn {
  position: fixed;
  bottom: 24px;
  right: 40px;
  z-index: 100000;
  transition: bottom 0.2s ease;
}
/* Lift the button just clear of the Webex call-control bar whenever it's showing
   (any active Webex connection now keeps the bar up, not just an active call).
   CallControlBar.vue publishes its own live height as --webex-call-bar-height
   since an error row or wrap-up controls can grow it past a guessed constant. */
.feedback-btn--call {
  bottom: calc(var(--webex-call-bar-height, 48px) + 8px);
}

/* Collapse to just the icon; the label slides out from behind it on hover. */
.feedback-btn .v-btn__prepend {
  margin-inline-end: 0;
}
.feedback-btn-text {
  display: inline-block;
  max-width: 0;
  margin-left: 0;
  opacity: 0;
  overflow: hidden;
  white-space: nowrap;
  transition: max-width 0.3s ease, margin-left 0.3s ease, opacity 0.2s ease;
}
.feedback-btn:hover .feedback-btn-text {
  max-width: 150px;
  margin-left: 8px;
  opacity: 1;
}

/* Custom Sentry bug button styles - commented out to use default auto-inject
.sentry-bug-button {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 100000;
  transition: all 0.2s ease-in-out;
}
.sentry-bug-button:hover {
  transform: scale(1.1);
}
.sentry-bug-button:hover :deep(.v-btn__overlay) {
  opacity: 0.12;
}
*/
</style>
